import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ctaData, contactData } from '../data/mockData';

const CTA = () => {
  const handleWhatsAppClick = () => {
    window.open(contactData.socialLinks.whatsapp, '_blank');
  };

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-800 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-400/10 rounded-full blur-2xl animate-ping"></div>
      </div>

      {/* Floating elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-white/20 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Urgency Badge */}
          <div className="mb-8">
            <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-6 py-3 text-lg animate-pulse">
              <div className="w-3 h-3 bg-red-400 rounded-full mr-3 animate-ping"></div>
              {ctaData.urgencyText}
            </Badge>
          </div>

          {/* Main CTA Title */}
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 leading-tight">
            <span className="bg-gradient-to-r from-orange-400 via-red-500 to-pink-500 bg-clip-text text-transparent">
              {ctaData.title.split('…')[0]}…
            </span>
            <br />
            <span className="text-white text-4xl md:text-5xl">
              {ctaData.title.split('…')[1]}
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
            {ctaData.subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Button 
              size="lg" 
              onClick={handleWhatsAppClick}
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-12 py-6 text-xl font-bold transform hover:scale-110 transition-all duration-300 shadow-2xl shadow-green-500/25 animate-pulse"
            >
              <span className="mr-3 text-2xl">📱</span>
              {ctaData.buttonText}
            </Button>
            
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white px-12 py-6 text-xl font-bold transform hover:scale-110 transition-all duration-300"
            >
              <span className="mr-3 text-2xl">📧</span>
              Email Kar!
            </Button>
          </div>

          {/* Feature Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
              <div className="text-4xl mb-3">⚡</div>
              <div className="text-white font-bold mb-2">24 Hour Delivery</div>
              <div className="text-gray-400 text-sm">Rush projects ke liye!</div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
              <div className="text-4xl mb-3">💯</div>
              <div className="text-white font-bold mb-2">100% Satisfaction</div>
              <div className="text-gray-400 text-sm">Ya phir paisa wapas!</div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-6 border border-white/10">
              <div className="text-4xl mb-3">🆓</div>
              <div className="text-white font-bold mb-2">Free Consultation</div>
              <div className="text-gray-400 text-sm">Pehle baat, phir rate!</div>
            </div>
          </div>

          {/* Social Proof */}
          <div className="text-center">
            <p className="text-gray-400 mb-4">Trusted by 200+ clients across India</p>
            <div className="flex justify-center items-center space-x-2">
              <div className="flex -space-x-2">
                {[1,2,3,4,5].map((i) => (
                  <div key={i} className="w-10 h-10 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full border-2 border-white flex items-center justify-center text-white font-bold text-sm">
                    {i}
                  </div>
                ))}
              </div>
              <span className="text-gray-400 ml-4">+195 more happy clients!</span>
            </div>
          </div>

          {/* Bottom decoration */}
          <div className="mt-16 flex justify-center">
            <div className="flex space-x-4">
              {['🎨', '💻', '🚀', '✨', '💎'].map((emoji, index) => (
                <div
                  key={index}
                  className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-2xl hover:scale-110 transition-transform duration-300 animate-bounce"
                  style={{ animationDelay: `${index * 0.2}s` }}
                >
                  {emoji}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for floating animation */}
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px) rotate(0deg);
            opacity: 0.5;
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
            opacity: 1;
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </section>
  );
};

export default CTA;